import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import './admin-panel.css';

export default function ReviewTab() {
  const navigate = useNavigate();
  const [packages, setPackages] = useState([]);
  const [status, setStatus] = useState('loading'); // 'loading' | 'ready' | 'error'

  const load = () => {
    setStatus('loading');
    axios
      .get('/api/packages/', { params: { status: 'review' } })
      .then((response) => {
        setPackages(response.data?.packages ?? []);
        setStatus('ready');
      })
      .catch(() => setStatus('error'));
  };

  useEffect(load, []);

  const handleApprove = async (pkg) => {
    try {
      await axios.post(`/api/packages/?id=${pkg.id}&_method=PATCH`, { status: 'published' });
      toast.success(`"${pkg.title}" published.`);
      load();
    } catch {
      toast.error('Could not publish the package. Please try again.');
    }
  };

  const handleDelete = async (pkg) => {
    if (!window.confirm(`Delete "${pkg.title}"? This cannot be undone.`)) return;
    try {
      await axios.post(`/api/packages/?id=${pkg.id}&_method=DELETE`);
      toast.info('Package deleted.');
      load();
    } catch {
      toast.error('Could not delete the package. Please try again.');
    }
  };

  const handleEdit = (pkg) => {
    navigate('/adminpanel/create', { state: { editId: pkg.id } });
  };

  return (
    <div>
      <div className="admin-topbar">
        <div>
          <h1>Review Packages</h1>
          <p>Verify title, description, images, inclusions and pricing before publishing.</p>
        </div>
      </div>

      {status === 'loading' && (
        <div className="admin-panel admin-empty-state">
          <p>Loading…</p>
        </div>
      )}

      {status === 'error' && (
        <div className="admin-panel admin-empty-state">
          <p>Could not load packages. Please try again later.</p>
        </div>
      )}

      {status === 'ready' && packages.length === 0 && (
        <div className="admin-panel admin-empty-state">
          <i className="fa fa-inbox" />
          <p>No packages waiting for review right now.</p>
        </div>
      )}

      {status === 'ready' &&
        packages.map((pkg) => (
          <div className="admin-review-card" key={pkg.id}>
            <div className="admin-review-thumb-wrap">
              <img
                src={pkg.images?.[0]}
                alt={pkg.title}
                className="admin-review-thumb"
              />
              {pkg.images?.length > 1 && (
                <span className="admin-review-thumb-count">
                  <i className="fa fa-images" /> {pkg.images.length}
                </span>
              )}
            </div>
            <div className="admin-review-body">
              <span className="admin-status-pill review">Pending Review</span>
              <h3 style={{ marginTop: 10 }}>{pkg.title}</h3>
              <div className="admin-review-meta">
                <span>
                  <i className="fa fa-map-marker-alt" />
                  {pkg.location}
                </span>
                <span>
                  <i className="fa fa-calendar-alt" />
                  {pkg.duration}
                </span>
                <span>
                  <i className="fa fa-user" />
                  {pkg.persons}
                </span>
                {pkg.price && (
                  <span>
                    <i className="fa fa-tag" />
                    {pkg.price}
                  </span>
                )}
                <span>
                  <i className="fa fa-star" />
                  {pkg.rating} / 5
                </span>
              </div>
              <p className="admin-review-desc">{pkg.description}</p>
              {pkg.included?.length > 0 && (
                <ul className="admin-review-included">
                  {pkg.included.map((item, i) => (
                    <li key={i}>
                      <i className="fa fa-check" />
                      {item}
                    </li>
                  ))}
                </ul>
              )}
              <div className="admin-review-actions">
                <button
                  className="admin-btn admin-btn-outline admin-btn-sm"
                  onClick={() => handleEdit(pkg)}
                >
                  <i className="fa fa-pen" /> Edit
                </button>
                <button
                  className="admin-btn admin-btn-success admin-btn-sm"
                  onClick={() => handleApprove(pkg)}
                >
                  <i className="fa fa-check" /> Approve &amp; Publish
                </button>
                <button
                  className="admin-btn admin-btn-danger admin-btn-sm"
                  onClick={() => handleDelete(pkg)}
                >
                  <i className="fa fa-trash" /> Delete
                </button>
              </div>
            </div>
          </div>
        ))}
    </div>
  );
}
